import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Package, Check, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_TYPES = {
  donuts: { label: "Donuts", color: "bg-pink-100 text-pink-800" },
  meat_pies: { label: "Meat Pies", color: "bg-amber-100 text-amber-800" },
  sausage_rolls: { label: "Sausage Rolls", color: "bg-orange-100 text-orange-800" },
  hotdog_rolls: { label: "Hotdog Rolls", color: "bg-red-100 text-red-800" },
  egg_rolls: { label: "Egg Rolls", color: "bg-yellow-100 text-yellow-800" }
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Form state
  const [name, setName] = useState("");
  const [productType, setProductType] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [costPerUnit, setCostPerUnit] = useState("");

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async () => {
    if (!name || !productType || !unitPrice) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      await axios.post(`${API}/products`, {
        name,
        product_type: productType,
        unit_price: parseFloat(unitPrice),
        cost_per_unit: parseFloat(costPerUnit) || 0
      });
      toast.success("Product added successfully");
      setIsDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error("Failed to add product");
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;

    try {
      await axios.put(`${API}/products/${editingProduct.id}`, {
        name: name || undefined,
        unit_price: unitPrice ? parseFloat(unitPrice) : undefined,
        cost_per_unit: costPerUnit ? parseFloat(costPerUnit) : undefined
      });
      toast.success("Product updated successfully");
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const startEditing = (product) => {
    setEditingProduct(product);
    setName(product.name);
    setUnitPrice(product.unit_price.toString());
    setCostPerUnit(product.cost_per_unit.toString());
  };

  const cancelEditing = () => {
    setEditingProduct(null);
    resetForm();
  };

  const resetForm = () => {
    setName("");
    setProductType("");
    setUnitPrice("");
    setCostPerUnit("");
  };

  // Group products by type
  const productsByType = products.reduce((acc, product) => {
    if (!acc[product.product_type]) acc[product.product_type] = [];
    acc[product.product_type].push(product);
    return acc;
  }, {});

  return (
    <div className="space-y-8 animate-fade-in" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 tracking-tight">Products</h1>
          <p className="text-stone-500 mt-1">Manage your product catalog and pricing</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-product-dialog">
            <DialogHeader>
              <DialogTitle>Add New Product</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Product Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Chocolate Donut"
                  data-testid="product-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Product Type</Label>
                <Select value={productType} onValueChange={setProductType}>
                  <SelectTrigger data-testid="product-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRODUCT_TYPES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Selling Price (NGN)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                  placeholder="Enter selling price"
                  data-testid="product-price-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Cost Per Unit (NGN) - optional</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={costPerUnit}
                  onChange={(e) => setCostPerUnit(e.target.value)}
                  placeholder="Enter production cost"
                  data-testid="product-cost-input"
                />
              </div>

              <Button 
                onClick={handleAddProduct} 
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="submit-product-btn"
              >
                Add Product
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Product Type Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {Object.entries(PRODUCT_TYPES).map(([key, { label, color }]) => (
          <Card key={key} className="card-hover" data-testid={`product-type-card-${key}`}>
            <CardContent className="p-4">
              <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${color} mb-2`}>
                {label}
              </div>
              <p className="text-2xl font-bold text-stone-900">
                {productsByType[key]?.length || 0}
              </p>
              <p className="text-xs text-stone-500">products</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Products Table */}
      <Card data-testid="products-table">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-600" />
            All Products
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-stone-500">Loading...</div>
          ) : products.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              No products yet. Click "Add Product" to create your first product.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Selling Price</TableHead>
                  <TableHead className="text-right">Cost/Unit</TableHead>
                  <TableHead className="text-right">Profit/Unit</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => {
                  const profitPerUnit = product.unit_price - product.cost_per_unit;
                  const isEditing = editingProduct?.id === product.id;
                  
                  return (
                    <TableRow key={product.id} className="table-row-hover" data-testid={`product-row-${product.id}`}>
                      <TableCell className="font-medium">
                        {isEditing ? (
                          <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="h-8 w-40"
                            data-testid={`edit-name-${product.id}`}
                          />
                        ) : (
                          product.name
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`badge ${PRODUCT_TYPES[product.product_type]?.color || 'bg-stone-100 text-stone-800'}`}>
                          {PRODUCT_TYPES[product.product_type]?.label || product.product_type}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {isEditing ? (
                          <Input
                            type="number"
                            value={unitPrice}
                            onChange={(e) => setUnitPrice(e.target.value)}
                            className="h-8 w-24 ml-auto"
                            data-testid={`edit-price-${product.id}`}
                          />
                        ) : (
                          formatCurrency(product.unit_price)
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isEditing ? (
                          <Input
                            type="number"
                            value={costPerUnit}
                            onChange={(e) => setCostPerUnit(e.target.value)}
                            className="h-8 w-24 ml-auto"
                            data-testid={`edit-cost-${product.id}`}
                          />
                        ) : (
                          formatCurrency(product.cost_per_unit)
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-semibold ${profitPerUnit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(profitPerUnit)}
                      </TableCell>
                      <TableCell className="text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleUpdateProduct}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid={`save-product-${product.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={cancelEditing}
                              className="text-stone-600 hover:text-stone-700 hover:bg-stone-50"
                              data-testid={`cancel-edit-${product.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditing(product)}
                              className="text-stone-600 hover:text-stone-700 hover:bg-stone-50"
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteProduct(product.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
